/* Andes search engine — shared by every search box of the store (header overlay, collection toolbar,
   search page, help center).
   - Index: /collections/all?view=aa-index (templates/collection.aa-index.liquid), cached 15 min per tab.
   - Synonyms: built-in Chilean Spanish groups + merchant groups (Configuración del tema › Andes · Búsqueda).
   - Matching ignores case, accents and plurals, completes the word being typed and tolerates one typo.
   - Ranking (field priority): title 100 · tags 45 · type/category/collection 30 · metafields 18 · description 7,
     synonyms count 82 %, all words must match (falls back to "any word" when nothing matches all),
     bonus when the title holds the whole phrase or starts with it.
   - Search page: queries sent to /search are expanded with OR (Shopify storefront search syntax) so
     synonyms also reach Shopify's results, which are then re-ranked with the same priorities. */
(() => {
  if (window.AASearch) return;

  const BUILTIN = `
gafas, lentes, anteojos, antiparras, gafas de sol
linterna, linterna led, frontal, linterna frontal, linterna de cabeza, linterna manos libres
iluminacion, luz, lampara, foco, ampolleta, luminaria
power bank, powerbank, bateria externa, bateria portatil, cargador portatil, cargador solar
parlante, altavoz, bocina, speaker, equipo de musica
microfono, karaoke
carpa, tienda de campana, tienda de campaña
mochila, morral, backpack
silla, asiento, piso, silla plegable, silla de camping
cortasetos, cortacerco, tijera de podar, podadora, tijera
orilladora, desbrozadora, cortabordes, bordeadora, desmalezadora
sopladora, soplador, aspiradora de hojas
motosierra, sierra, sierra electrica
inalambrico, sin cable, sin cables
camping, acampar, campamento, outdoor, aire libre
asado, parrilla, quincho, barbecue
fiesta, carrete, junta, celebracion
auto, carro, coche, vehiculo, camioneta, automovil
compresor, inflador, bomba de aire, inflar
partidor, arrancador, partidor de bateria, jump starter, booster
tripode, soporte, palo selfie, selfie stick, estabilizador
celular, telefono, smartphone, movil
regalo, obsequio, presente, sorpresa
mosca, moscas, mosquito, zancudo, insecto, bichos, antimoscas
ventilador, abanico
lampara colgante, colgante, lampara de techo, plafon, luz de techo
jardin, patio, huerto, pasto, cesped
solar, panel solar, energia solar
picnic, merienda, almuerzo al aire libre
grabacion, grabar, streaming, vlog, video, youtube, tiktok, creador de contenido
herramienta, herramientas, kit de herramientas
emergencia, emergencias, terremoto, corte de luz, apagon, kit de emergencia
pesca, pescar, pesca nocturna
trekking, senderismo, caminata, excursion, hiking, montana, montaña
nieve, ski, esqui, invierno, frio
playa, verano, piscina
relajacion, relajarse, descanso, dormir
termo, botella, termica, hidratacion
reloj, smartwatch, reloj inteligente
audifonos, auriculares, fonos, headphones
dron, drone, cuadricoptero
envio, despacho, entrega, delivery, enviar, llegar, llega
devolucion, devolver, reembolso, cambio, cambiar, retracto
pago, pagar, tarjeta, cuotas, medios de pago, debito, credito
boleta, factura, documento tributario
garantia, falla, defecto, roto, danado, dañado, reclamo
seguimiento, tracking, rastrear, rastreo, donde esta mi pedido, estado del pedido, mi pedido
contacto, ayuda, soporte, atencion al cliente
oferta, ofertas, descuento, promocion, promo, rebaja, liquidacion, sale
barato, economico, bajo precio
`;
  const STOP = new Set('de del la las el los un una unos unas y o u e a al en con para por sin mi mis tu tus su sus que lo le les me se es son muy mas the of for and'.split(' '));
  const W = { t: 100, g: 45, y: 30, m: 18, d: 7 };
  const SYN = 0.82;
  const HEAD = 0.62;
  const SPREAD = 0.15;

  const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ñ/g, 'n').replace(/[^a-z0-9]+/g, ' ').trim();
  const stem = (w) => {
    if (w.length > 4 && w.endsWith('ces')) return w.slice(0, -3) + 'z';
    if (w.length > 3 && w.endsWith('s') && !/(ss|us|is)$/.test(w)) {
      w = w.slice(0, -1);
      if (w.length > 4 && /[rlnd]e$/.test(w)) w = w.slice(0, -1);
    }
    return w;
  };
  const words = (s) => norm(s).split(' ').filter((w) => w && !STOP.has(w));
  const stems = (s) => words(s).map(stem);

  // ---- Synonym groups: each entry is a phrase (1–4 words), stored as stemmed tokens
  let groups = [];
  let byKey = new Map();
  let byHead = new Map();
  function buildSynonyms() {
    let extra = '';
    try { extra = JSON.parse(document.getElementById('AaSearchSynonyms')?.textContent || '""') || ''; } catch (e) { extra = ''; }
    const lines = (BUILTIN + '\n' + extra).split(/\n|;/).map((l) => l.trim()).filter(Boolean);
    groups = [];
    byKey = new Map();
    byHead = new Map();
    for (const line of lines) {
      const g = [...new Set(line.split(',').map((p) => p.trim()).filter(Boolean))].map((raw) => ({ raw: norm(raw), tokens: stems(raw) })).filter((e) => e.tokens.length);
      if (g.length < 2) continue;
      const gi = groups.push(g) - 1;
      for (const e of g) {
        const key = e.tokens.join(' ');
        if (!byKey.has(key)) byKey.set(key, new Set());
        byKey.get(key).add(gi);
        if (e.tokens.length > 1) {
          if (!byHead.has(e.tokens[0])) byHead.set(e.tokens[0], new Set());
          byHead.get(e.tokens[0]).add(gi);
        }
      }
    }
  }
  buildSynonyms();

  // Query → terms (phrases from the dictionary are kept together), each with its variants
  function parse(q) {
    const raw = words(q);
    const tok = raw.map(stem);
    const terms = [];
    for (let i = 0; i < tok.length; ) {
      let took = 0;
      for (let n = Math.min(4, tok.length - i); n >= 2 && !took; n--) {
        if (byKey.has(tok.slice(i, i + n).join(' '))) took = n;
      }
      const n = took || 1;
      const key = tok.slice(i, i + n).join(' ');
      const variants = [{ tokens: tok.slice(i, i + n), raw: raw.slice(i, i + n).join(' '), syn: false, w: 1 }];
      const seen = new Set([key]);
      const add = (gi, w) => {
        for (const e of groups[gi]) {
          const k = e.tokens.join(' ');
          if (seen.has(k)) continue;
          seen.add(k);
          variants.push({ tokens: e.tokens, raw: e.raw, syn: true, w });
        }
      };
      const full = byKey.get(key) || new Set();
      full.forEach((gi) => add(gi, SYN));
      // A single word that opens a dictionary phrase ("bateria" → "bateria externa" = power bank) brings
      // that group too, with less weight than a real synonym
      if (n === 1 && key.length >= 4) (byHead.get(key) || new Set()).forEach((gi) => { if (!full.has(gi)) add(gi, HEAD); });
      terms.push({ raw: raw.slice(i, i + n).join(' '), variants, last: i + n === tok.length });
      i += n;
    }
    return terms;
  }

  // ---- Index
  const fieldOf = (text) => {
    const t = stems(text);
    return { list: t, set: new Set(t), joined: ` ${t.join(' ')} ` };
  };
  let index = null;
  let loading = null;
  let df = new Map();
  let nProducts = 0;
  const CACHE = 'aa-search-index-v3';
  function prepare(json) {
    const doc = (kind, o) => ({ kind, o, f: { t: fieldOf(o.t), g: fieldOf(o.g || ''), y: fieldOf(o.y || ''), m: fieldOf(o.m || ''), d: fieldOf(o.d || '') } });
    const products = (json.products || []).map((o, i) => Object.assign(doc('products', o), { order: i }));
    // Document frequency per token (IDF): words present in most products ("devolución", "envío") weigh less
    df = new Map();
    for (const p of products) {
      const seen = new Set();
      for (const key of ['t', 'g', 'y', 'm', 'd']) p.f[key].set.forEach((tk) => seen.add(tk));
      seen.forEach((tk) => df.set(tk, (df.get(tk) || 0) + 1));
    }
    nProducts = products.length;
    return {
      products,
      collections: (json.collections || []).map((o, i) => Object.assign(doc('collections', o), { order: i })),
      articles: (json.articles || []).map((o, i) => Object.assign(doc('articles', o), { order: i })),
      pages: (json.pages || []).map((o, i) => Object.assign(doc('pages', o), { order: i })),
    };
  }
  function ready() {
    if (index) return Promise.resolve(index);
    if (loading) return loading;
    loading = (async () => {
      try {
        const c = JSON.parse(sessionStorage.getItem(CACHE) || 'null');
        if (c && Date.now() - c.at < 15 * 60 * 1000 && Array.isArray(c.json?.products)) return (index = prepare(c.json));
      } catch (e) { /* no cache */ }
      const root = window.Shopify?.routes?.root || '/';
      // No "Accept: application/json" header: Shopify would answer with its own collection JSON instead of the template
      const res = await fetch(`${root}collections/all?view=aa-index`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('index ' + res.status);
      const json = JSON.parse(await res.text());
      if (!json || !Array.isArray(json.products)) throw new Error('index format');
      try { sessionStorage.setItem(CACHE, JSON.stringify({ at: Date.now(), json })); } catch (e) { /* storage full */ }
      return (index = prepare(json));
    })();
    loading.catch(() => { loading = null; });
    return loading;
  }

  // ---- Matching
  function lev1(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return false;
    const prev = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
      let rowMin = i;
      let diag = prev[0];
      prev[0] = i;
      for (let j = 1; j <= b.length; j++) {
        const tmp = prev[j];
        prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
        diag = tmp;
        rowMin = Math.min(rowMin, prev[j]);
      }
      if (rowMin > max) return false;
    }
    return prev[b.length] <= max;
  }
  // mode.prefix: complete the word being typed · mode.fuzzy: one/two typos (only when nothing matches exactly)
  function quality(v, field, key, mode) {
    if (v.tokens.length > 1) {
      if (field.joined.includes(` ${v.tokens.join(' ')} `)) return 1;
      return v.tokens.every((t) => field.set.has(t)) ? 0.75 : 0;
    }
    const t = v.tokens[0];
    if (field.set.has(t)) return 1;
    if (v.syn) return 0;
    if (mode.prefix && t.length >= 3 && field.list.some((w) => w.startsWith(t))) return 0.72;
    if (mode.fuzzy && (key === 't' || key === 'g' || key === 'y') && t.length >= 5) {
      const max = t.length >= 8 ? 2 : 1;
      if (field.list.some((w) => lev1(t, w, max))) return 0.5;
    }
    return 0;
  }
  // IDF-like factor (products only): a word found in most products barely helps to rank them
  function rarity(v, kind) {
    if (kind !== 'products' || !nProducts) return 1;
    const share = Math.max(...v.tokens.map((t) => (df.get(t) || 0) / nProducts));
    return share > 0.6 ? 0.3 : share > 0.35 ? 0.65 : 1;
  }
  // Best field counts in full; every other field where the concept also appears adds 15 % of its value,
  // so a product that is about the word (title + tags + specs) beats one that only mentions it once.
  function termScore(doc, term, mode) {
    const perField = { t: 0, g: 0, y: 0, m: 0, d: 0 };
    for (const v of term.variants) {
      const r = rarity(v, doc.kind);
      const w = v.w ?? (v.syn ? SYN : 1);
      for (const key of ['t', 'g', 'y', 'm', 'd']) {
        const q = quality(v, doc.f[key], key, mode);
        if (q) perField[key] = Math.max(perField[key], W[key] * q * w * r);
      }
    }
    const values = Object.values(perField).sort((a, b) => b - a);
    return values[0] ? values[0] + SPREAD * values.slice(1).reduce((s, x) => s + x, 0) : 0;
  }
  function scoreDoc(doc, terms, phrase, modes) {
    let total = 0;
    let matched = 0;
    terms.forEach((term, i) => {
      const best = termScore(doc, term, modes[i]);
      if (best) matched += 1;
      total += best;
    });
    if (!matched) return { score: 0, matched: 0 };
    const title = doc.f.t.joined;
    if (phrase && phrase.includes(' ') && title.includes(` ${phrase} `)) total += 60;
    if (phrase && title.startsWith(` ${phrase}`)) total += 25;
    return { score: total, matched };
  }
  function rank(list, terms, phrase, opts) {
    const pool = list.filter((doc) => !(opts.available && doc.kind === 'products' && doc.o.a === false));
    // Per term: exact words (and synonyms) first; the word being typed may be completed; typo tolerance
    // only switches on for a term that has no exact or completed match anywhere in this list.
    const modes = terms.map((term) => {
      const exact = { prefix: false, fuzzy: false };
      if (pool.some((doc) => termScore(doc, term, exact))) return { prefix: term.last, fuzzy: false };
      const pre = { prefix: true, fuzzy: false };
      if (pool.some((doc) => termScore(doc, term, pre))) return pre;
      return { prefix: true, fuzzy: true };
    });
    const scored = [];
    for (const doc of pool) {
      const r = scoreDoc(doc, terms, phrase, modes);
      if (r.matched) scored.push({ doc, ...r });
    }
    let hits = scored.filter((s) => s.matched === terms.length);
    // Nothing has every word: show what has the most words, best first
    if (!hits.length && terms.length > 1) hits = scored.map((s) => ({ ...s, score: s.score * (s.matched / terms.length) }));
    hits.sort((a, b) => b.score - a.score || a.doc.order - b.doc.order);
    return hits;
  }

  function search(q, opts = {}) {
    const terms = parse(q);
    const empty = { products: [], collections: [], articles: [], pages: [], terms };
    if (!index || !terms.length) return empty;
    const phrase = stems(q).join(' ');
    const limit = opts.limit || 10;
    const out = { terms };
    for (const kind of ['products', 'collections', 'articles', 'pages']) {
      out[kind] = rank(index[kind], terms, phrase, { available: opts.available !== false })
        .slice(0, kind === 'products' ? opts.productLimit || limit : limit)
        .map((h) => {
          const o = h.doc.o;
          return { title: o.t, url: o.u, image: o.i || '', price: o.p, compare_at_price: o.c, available: o.a !== false, author: o.au, handle: o.h, score: Math.round(h.score) };
        });
    }
    return out;
  }
  // handle → score for every product that matches (used to filter and order product grids)
  function scoreProducts(q, opts = {}) {
    const terms = parse(q);
    const map = new Map();
    if (!index || !terms.length) return map;
    const phrase = stems(q).join(' ');
    for (const h of rank(index.products, terms, phrase, { available: opts.available === true })) map.set(h.doc.o.h, h.score);
    return map;
  }
  // Words to highlight (the query plus its synonyms)
  function highlightWords(q) {
    return [...new Set(parse(q).flatMap((t) => t.variants.map((v) => v.raw)).filter((w) => w.length > 1))];
  }
  // Does a free text match the query (all terms, synonyms allowed)? Used by the help center.
  function matchesText(q, text) {
    const terms = parse(q);
    if (!terms.length) return true;
    const field = fieldOf(text);
    return terms.every((term) => term.variants.some((v) => quality(v, field, 't', { prefix: term.last, fuzzy: false }) > 0));
  }
  // Shopify storefront search query with synonyms: (gafas OR lentes OR anteojos) solar
  function shopifyQuery(q) {
    const terms = parse(q);
    if (!terms.some((t) => t.variants.length > 1)) return q.trim();
    const part = (v) => (v.raw.includes(' ') ? `"${v.raw}"` : v.raw);
    return terms
      .map((t) => {
        const vs = t.variants.slice(0, 8).map(part);
        return vs.length > 1 ? `(${vs.join(' OR ')})` : vs[0];
      })
      .join(' ');
  }
  function searchUrl(q, extra = {}) {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({ ...extra });
    const expanded = shopifyQuery(q);
    params.set('q', expanded);
    if (expanded !== q.trim()) params.set('aa_q', q.trim());
    if (!params.has('options[prefix]')) params.set('options[prefix]', 'last');
    return `${root}search?${params}`;
  }

  window.AASearch = { ready, search, scoreProducts, highlightWords, matchesText, shopifyQuery, searchUrl, parse, norm, stem };

  // ---- Every search form / search link of the site goes through the synonym expansion
  const isSearchPath = (url) => /\/search$/.test(url.pathname);
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.matches('[data-aa-search-form]') || e.defaultPrevented) return;
    if ((form.method || 'get').toLowerCase() !== 'get') return;
    let url;
    try { url = new URL(form.action, location.href); } catch (err) { return; }
    if (!isSearchPath(url)) return;
    const data = new FormData(form);
    const q = String(data.get('q') || '').trim();
    if (!q || /\sOR\s/.test(q)) return;
    if (shopifyQuery(q) === q) return;
    const extra = {};
    for (const [k, v] of data.entries()) if (k !== 'q' && k !== 'aa_q') extra[k] = String(v);
    const target = searchUrl(q, extra);
    e.preventDefault();
    const a = document.createElement('a');
    a.href = target;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, true);
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a) return;
    let url;
    try { url = new URL(a.href, location.href); } catch (err) { return; }
    if (url.origin !== location.origin || !isSearchPath(url)) return;
    const q = url.searchParams.get('q');
    if (!q || url.searchParams.has('aa_q') || /\sOR\s/.test(q)) return;
    const expanded = shopifyQuery(q);
    if (expanded === q.trim()) return;
    url.searchParams.set('q', expanded);
    url.searchParams.set('aa_q', q.trim());
    a.href = url.toString();
  }, true);

  // ---- Search page: show the shopper's own words and re-rank the results with our priorities
  function onSearchPage() {
    if (!isSearchPath(new URL(location.href))) return;
    const params = new URLSearchParams(location.search);
    const original = (params.get('aa_q') || params.get('q') || '').trim();
    if (!original) return;
    const shown = params.get('q') || '';
    if (params.get('aa_q')) {
      document.querySelectorAll('input[name="q"]').forEach((i) => { if (i.value === shown) i.value = original; });
      document.querySelectorAll('[data-aa-terms]').forEach((el) => { el.textContent = `“${original}”`; });
      // Any other place that printed the expanded query (titles, counters)
      const walker = document.createTreeWalker(document.querySelector('main') || document.body, NodeFilter.SHOW_TEXT);
      for (let n = walker.nextNode(); n; n = walker.nextNode()) if (n.nodeValue.includes(shown)) n.nodeValue = n.nodeValue.split(shown).join(original);
      if (document.title.includes(shown)) document.title = document.title.split(shown).join(original);
    }
    const reorder = () => {
      const scores = scoreProducts(original, { available: false });
      if (!scores.size) return;
      const lists = new Set();
      document.querySelectorAll('main product-card').forEach((card) => {
        const li = card.closest('li') || card;
        if (li.parentElement) lists.add(li.parentElement);
      });
      lists.forEach((list) => {
        const items = [...list.children];
        const handleOf = (li) => (li.querySelector('a[href*="/products/"]')?.getAttribute('href') || '').match(/\/products\/([^/?#]+)/)?.[1] || '';
        const sorted = items
          .map((li, i) => ({ li, i, s: scores.get(handleOf(li)) || 0 }))
          .sort((a, b) => b.s - a.s || a.i - b.i)
          .map((x) => x.li);
        // Only touch the DOM when the order really changes (moving nodes would re-trigger the observer)
        if (sorted.some((li, i) => li !== items[i])) sorted.forEach((li) => list.appendChild(li));
      });
    };
    ready().then(reorder).catch(() => {});
    const main = document.querySelector('main');
    if (main) {
      let t;
      new MutationObserver(() => { clearTimeout(t); t = setTimeout(() => index && reorder(), 200); }).observe(main, { childList: true, subtree: true });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onSearchPage);
  else onSearchPage();
})();
